/**
 * Magazine Flipbook
 * Crea un effetto libro sfogliabile per le riviste
 */

class MagazineFlipbook {
  constructor(container) {
    this.container = container;
    this.pagesContainer = container.querySelector('[data-flipbook-pages]');
    this.pages = Array.from(container.querySelectorAll('.flipbook-page'));
    this.currentPage = 0;
    this.isAnimating = false;
    
    // Elementi UI
    this.prevBtn = container.querySelector('[data-flipbook-prev]');
    this.nextBtn = container.querySelector('[data-flipbook-next]');
    this.firstBtn = container.querySelector('[data-flipbook-first]');
    this.lastBtn = container.querySelector('[data-flipbook-last]');
    this.currentCounter = container.querySelector('[data-flipbook-current]');
    this.totalCounter = container.querySelector('[data-flipbook-total]');
    
    this.init();
  }
  
  init() {
    // Setup iniziale
    this.totalPages = this.pages.length;
    
    if (this.totalCounter) {
      this.totalCounter.textContent = this.totalPages;
    }
    
    // Posiziona tutte le pagine - sempre singole
    this.layoutPages();
    
    // Event listeners
    this.prevBtn?.addEventListener('click', () => this.previousPage());
    this.nextBtn?.addEventListener('click', () => this.nextPage());
    this.firstBtn?.addEventListener('click', () => this.goToPage(0));
    this.lastBtn?.addEventListener('click', () => this.goToPage(this.totalPages - 1));
    
    // Keyboard navigation (non mentre si scrive: le frecce muovono il cursore)
    document.addEventListener('keydown', (e) => {
      if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey) return;
      const t = e.target;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
      if (e.key === 'ArrowLeft') this.previousPage();
      if (e.key === 'ArrowRight') this.nextPage();
    });

    // Dopo il caricamento della pagina, in un momento libero, prepara le due
    // pagine successive così sfogliare è immediato.
    const preload = () => {
      const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 200));
      idle(() => { this.loadPage(this.currentPage + 1); this.loadPage(this.currentPage + 2); });
    };
    if (document.readyState === 'complete') preload();
    else window.addEventListener('load', preload, { once: true });
    
    // Touch/swipe support
    this.addSwipeSupport();
    
    // Mostra la prima pagina
    this.showPage(0);
  }
  
  // Le pagine dopo la prima hanno l'immagine in data-src/data-srcset:
  // l'indirizzo vero si assegna solo quando la pagina serve.
  loadPage(index) {
    const page = this.pages[index];
    const img = page && page.querySelector('img[data-src]');
    if (!img) return;
    img.addEventListener('load', () => img.classList.remove('is-loading'), { once: true });
    img.addEventListener('error', () => img.classList.remove('is-loading'), { once: true });
    if (img.dataset.srcset) img.srcset = img.dataset.srcset;
    img.src = img.dataset.src;
    img.removeAttribute('data-src');
    img.removeAttribute('data-srcset');
  }

  loadAround(index) {
    this.loadPage(index);
    this.loadPage(index + 1);
    this.loadPage(index - 1);
  }

  layoutPages() {
    this.pages.forEach((page, index) => {
      page.style.setProperty('--page-index', index);
      // Tutte le pagine sono singole
      page.classList.add('flipbook-page--single');
    });
  }
  
  showPage(pageIndex) {
    // Mostra immediatamente la pagina senza animazione
    this.pages.forEach((page, index) => {
      page.classList.remove('is-current', 'is-hidden', 'flip-left', 'flip-right');
      if (index === pageIndex) {
        page.classList.add('is-current');
      } else {
        page.classList.add('is-hidden');
      }
    });
    
    this.currentPage = pageIndex;
    // All'avvio si carica solo la pagina mostrata: le vicine arrivano dopo il
    // load (vedi preload in init), per non rubare banda alla copertina.
    this.loadPage(pageIndex);
    this.updateView();
  }
  
  nextPage() {
    if (this.isAnimating) return;
    
    // Una pagina alla volta
    if (this.currentPage < this.totalPages - 1) {
      this.goToPage(this.currentPage + 1);
    }
  }
  
  previousPage() {
    if (this.isAnimating) return;
    
    // Una pagina alla volta
    if (this.currentPage > 0) {
      this.goToPage(this.currentPage - 1);
    }
  }
  
  goToPage(pageIndex) {
    if (this.isAnimating || pageIndex === this.currentPage) return;
    if (pageIndex < 0 || pageIndex >= this.totalPages) return;
    
    this.isAnimating = true;
    
    const direction = pageIndex > this.currentPage ? 'forward' : 'backward';
    this.loadAround(pageIndex);
    
    // Rimuovi tutte le classi di stato
    this.pages.forEach(page => {
      page.classList.remove('is-current', 'is-hidden', 'flip-left', 'flip-right');
    });
    
    // Mostra sempre una pagina alla volta
    this.pages.forEach((page, index) => {
      if (index === pageIndex) {
        page.classList.add('is-current');
        page.classList.add(direction === 'forward' ? 'flip-left' : 'flip-right');
      } else {
        page.classList.add('is-hidden');
      }
    });
    
    // Aggiungi animazione flip
    this.container.classList.add('is-flipping', `flip-${direction}`);
    
    setTimeout(() => {
      this.currentPage = pageIndex;
      this.isAnimating = false;
      this.container.classList.remove('is-flipping', `flip-${direction}`);
      this.pages.forEach(page => {
        page.classList.remove('flip-left', 'flip-right');
      });
      this.updateView();
    }, 600);
  }
  
  updateView() {
    // Aggiorna counter
    if (this.currentCounter) {
      this.currentCounter.textContent = this.currentPage + 1;
    }
    
    // Aggiorna stato pulsanti
    if (this.prevBtn) {
      this.prevBtn.disabled = this.currentPage === 0;
    }
    
    if (this.nextBtn) {
      this.nextBtn.disabled = this.currentPage >= this.totalPages - 1;
    }
  }
  
  addSwipeSupport() {
    let touchStartX = 0;
    let touchEndX = 0;
    
    this.pagesContainer.addEventListener('touchstart', (e) => {
      touchStartX = e.changedTouches[0].screenX;
    }, { passive: true });
    
    this.pagesContainer.addEventListener('touchend', (e) => {
      touchEndX = e.changedTouches[0].screenX;
      this.handleSwipe();
    }, { passive: true });
    
    const handleSwipe = () => {
      const swipeThreshold = 50;
      const diff = touchStartX - touchEndX;
      
      if (Math.abs(diff) > swipeThreshold) {
        if (diff > 0) {
          // Swipe left - next page
          this.nextPage();
        } else {
          // Swipe right - previous page
          this.previousPage();
        }
      }
    };
    
    this.handleSwipe = handleSwipe;
  }
}

// Inizializza tutti i flipbook nella pagina
document.addEventListener('DOMContentLoaded', () => {
  const flipbooks = document.querySelectorAll('[data-magazine-flipbook]');
  flipbooks.forEach(flipbook => {
    new MagazineFlipbook(flipbook);
  });
});

// Export per uso esterno
if (typeof module !== 'undefined' && module.exports) {
  module.exports = MagazineFlipbook;
}
